/**
 * Top-level screen switcher. Hash routes → Home / Join / Match.
 * Global overlays (protocol mismatch, disconnect banner) live above
 * the route so they're visible from any screen.
 */

import type { ReactElement } from "react";
import { useHashRoute } from "./router/hashRoute.js";
import { HomeScreen } from "./ui/HomeScreen.js";
import { JoinScreen } from "./ui/JoinScreen.js";
import { MatchScreen } from "./ui/MatchScreen.js";
import { DemoMatchScreen } from "./ui/DemoMatchScreen.js";
import { SettingsScreen } from "./ui/SettingsScreen.js";
import { AccountScreen } from "./ui/AccountScreen.js";
import { PracticeSetupScreen } from "./ui/PracticeSetupScreen.js";
import { DummyPracticeScreen } from "./ui/DummyPracticeScreen.js";
import { navigate } from "./router/hashRoute.js";
import { ProtocolMismatchPanel } from "./ui/ProtocolMismatchPanel.js";
import { DisconnectBanner } from "./ui/DisconnectBanner.js";
import { ServerErrorBanner } from "./ui/ServerErrorBanner.js";
import { ConnectionDot } from "./ui/ConnectionDot.js";
import { SoundToggle } from "./sound/SoundToggle.js";
import { useStore } from "./store/context.js";

export function App(): ReactElement {
  const route = useHashRoute();
  const { state } = useStore();
  const mismatch = state.connection.kind === "protocolMismatch";
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {mismatch ? <ProtocolMismatchPanel /> : null}
      <DisconnectBanner />
      <ServerErrorBanner />
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 5,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <SoundToggle />
        <ConnectionDot />
      </div>
      {route.kind === "home" ? <HomeScreen /> : null}
      {route.kind === "join" ? <JoinScreen matchId={route.matchId} /> : null}
      {route.kind === "match" ? <MatchScreen /> : null}
      {route.kind === "demo" ? <DemoMatchScreen /> : null}
      {route.kind === "practice" ? <PracticeSetupScreen /> : null}
      {route.kind === "dummyPractice" ? <DummyPracticeScreen /> : null}
      {route.kind === "account" ? <AccountScreen /> : null}
      {route.kind === "settings" ? (
        <SettingsScreen
          onClose={() => {
            navigate({ kind: "home" });
          }}
        />
      ) : null}
    </div>
  );
}
